import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { DateInput } from '@/components/ui/date-input';
import { PregnancyDatingInputs } from '@/components/pregnancy/PregnancyDatingInputs';
import { formatDatingDate, parseDatingDate } from '@gene42/pedigree/pregnancy-dating';
import { useNodeGraphPreview } from '@/features/pedigree/NodeGraphPreviewContext';
import { projectNodeDemographics } from '@/features/pedigree/nodePreviewProjection';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { usePatientMutations } from '@/features/patient-record/hooks/usePatientMutations';
import type {
  DemographicsViewModel,
  InterpreterRequired,
  LifeStatus,
  PregnancyDatingBasis,
  SexCode,
} from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';
import { DraftFooter } from '../_shared/DraftFooter';
import { useFieldFlashStore, type FlashFieldKey } from '../_shared/fieldFlash';

import {
  AgeField,
  GenderIdentityField,
  InterpreterRequiredField,
  SelectField,
  SexField,
  TextField,
} from './PersonalInfoFields';

const LIFE_STATUS_OPTIONS: readonly LifeStatus[] = [
  'ALIVE',
  'ALIVE_AND_WELL',
  'DECEASED',
  'UNBORN',
  'STILL_BORN',
  'MISCARRIAGE',
  'ABORTED',
  'UNKNOWN',
];
/** Statuses that imply a date — Date of Death/Loss is shown only for these.
 *  ALIVE/UNBORN/UNKNOWN don't carry a date. */
const DECEASED_STATUSES: ReadonlySet<LifeStatus> = new Set([
  'DECEASED',
  'STILL_BORN',
  'MISCARRIAGE',
  'ABORTED',
]);
/** Statuses that carry a cause of death. Deliberately excludes ABORTED — a termination has no
 *  cause of death in this sense, and Enterprise gates the field the same way. */
const CAUSE_OF_DEATH_STATUSES: ReadonlySet<LifeStatus> = new Set([
  'DECEASED',
  'STILL_BORN',
  'MISCARRIAGE',
]);

/**
 * Default gender that "Same as sex" derives. Sex letter codes map 1:1 to the
 * BE's `GenderOptions` codes, except `U` which uses the long-form `'unknown'`.
 * Returns `null` when sex is unset (no auto-derivation possible).
 */
function deriveGenderFromSex(sex: SexCode | null): string | null {
  if (sex === null) return null;
  // U and the assigned-at-birth-only codes (INTERSEX/UNASSIGNED) have no gender-identity mirror →
  // derive 'unknown'; M/F/O map 1:1 to GenderOptions. (PTV2-1332)
  if (sex === 'U' || sex === 'INTERSEX' || sex === 'UNASSIGNED') return 'unknown';
  return sex;
}

/**
 * "Same as sex" should start checked when the saved gender already matches
 * the saved sex (or gender was never set). When it's a custom value, the
 * user has deliberately diverged — start unchecked so they can see/edit it.
 */
function deriveSameAsSex(sex: SexCode | null, gender: string | null): boolean {
  if (gender === null) return true;
  const derived = deriveGenderFromSex(sex);
  if (derived === null) return false;
  return gender === derived;
}

interface PersonalInfoCardProps {
  readonly guid: string;
  readonly demographics: DemographicsViewModel;
}

interface PersonalDraft {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly lastNameAtBirth: string | null;
  readonly dateOfBirth: string | null;
  readonly sex: SexCode | null;
  readonly genderIdentity: string | null;
  readonly lifeStatus: LifeStatus | null;
  readonly dateOfDeath: string | null;
  readonly causeOfDeath: string | null;
  /** PTV2-1586 dating pair — one anchor date plus the basis it was asserted on. */
  readonly pregnancyDatingAnchor: string | null;
  readonly pregnancyDatingBasis: PregnancyDatingBasis | null;
  readonly pregnancyDatingAsOf: string | null;
  readonly primaryCareProvider: string | null;
  readonly referringProvider: string | null;
  readonly interpreterRequired: InterpreterRequired | null;
  readonly interpreterLanguage: string | null;
  /** "Same gender as sex" shortcut — lives in the draft so dirtiness/reseed/autosave cover it. */
  readonly sameGenderAsSex: boolean;
}

/**
 * Personal info as a form: edit any field, then commit explicitly via
 * the Save button. No autosave on blur — accidental edits won't
 * persist. The dirty footer renders only when the draft has diverged
 * from the saved value.
 */
export function PersonalInfoCard({
  guid,
  demographics,
}: PersonalInfoCardProps): ReactElement {
  const { t } = useTranslation();
  const { updateDemographics } = usePatientMutations(guid);

  const initial = useMemo<PersonalDraft>(
    () => ({
      firstName: demographics.firstName,
      lastName: demographics.lastName,
      lastNameAtBirth: demographics.lastNameAtBirth,
      dateOfBirth: demographics.dateOfBirth,
      sex: demographics.sex,
      genderIdentity: demographics.genderIdentity,
      lifeStatus: demographics.lifeStatus,
      dateOfDeath: demographics.dateOfDeath,
      causeOfDeath: demographics.causeOfDeath,
      pregnancyDatingAnchor: demographics.pregnancyDatingAnchor,
      pregnancyDatingBasis: demographics.pregnancyDatingBasis,
      pregnancyDatingAsOf: demographics.pregnancyDatingAsOf,
      primaryCareProvider: demographics.primaryCareProvider,
      referringProvider: demographics.referringProvider,
      interpreterRequired: demographics.interpreterRequired,
      interpreterLanguage: demographics.interpreterLanguage,
      sameGenderAsSex: deriveSameAsSex(demographics.sex, demographics.genderIdentity),
    }),
    [demographics],
  );

  // PTV2-1157 — required-field flash. The node-editor match control raises a signal (keyed by guid)
  // when one of the four required demographics is missing; this card auto-expands and pulses a red
  // ring on the offending rows for ~1.8s. No-op on the plain patient record (signal never raised).
  const flashNonce = useFieldFlashStore((s) => s.nonce);
  const flashGuid = useFieldFlashStore((s) => s.guid);
  const flashFields = useFieldFlashStore((s) => s.fields);
  const [activeFlash, setActiveFlash] = useState<ReadonlySet<FlashFieldKey> | null>(null);
  const [forceOpenSignal, setForceOpenSignal] = useState(0);
  useEffect(() => {
    if (flashNonce === 0 || flashGuid !== guid) return;
    setActiveFlash(flashFields);
    setForceOpenSignal(flashNonce);
    const timer = setTimeout(() => {
      setActiveFlash(null);
    }, 1800);
    return () => {
      clearTimeout(timer);
    };
  }, [flashNonce, flashGuid, flashFields, guid]);
  const flashClass = (field: FlashFieldKey): string =>
    activeFlash?.has(field) === true
      ? 'rounded-md ring-2 ring-destructive ring-offset-2 ring-offset-background animate-pulse'
      : '';

  // PTV2-1327: the primitive owns the draft (reseed-safety, auto-registration, autosave of this
  // existing record). The draft is kept CANONICAL by its setters — date-of-death is cleared when the
  // status isn't a loss, interpreter language when it isn't "Yes" — so a plain compare is correct.
  // Gender is derived on SAVE only: when "same as sex" is on it mirrors sex (PD-138 needs a non-null
  // gender for pedigree rendering); the toggle itself lives in the draft so it counts as an edit
  // without the derived value marking a fresh record dirty on load.
  const toSavedVm = (d: PersonalDraft): DemographicsViewModel => ({
    ...demographics,
    firstName: normalizeText(d.firstName),
    lastName: normalizeText(d.lastName),
    lastNameAtBirth: normalizeText(d.lastNameAtBirth),
    dateOfBirth: d.dateOfBirth,
    sex: d.sex,
    genderIdentity: d.sameGenderAsSex ? deriveGenderFromSex(d.sex) : d.genderIdentity,
    lifeStatus: d.lifeStatus,
    dateOfDeath: d.dateOfDeath,
    causeOfDeath: normalizeText(d.causeOfDeath),
    pregnancyDatingAnchor: d.pregnancyDatingAnchor,
    pregnancyDatingBasis: d.pregnancyDatingBasis,
    pregnancyDatingAsOf: d.pregnancyDatingAsOf,
    primaryCareProvider: normalizeText(d.primaryCareProvider),
    referringProvider: normalizeText(d.referringProvider),
    interpreterRequired: d.interpreterRequired,
    interpreterLanguage: normalizeText(d.interpreterLanguage),
  });

  const form = useEditableDraft<PersonalDraft>({
    initial,
    label: 'Personal information',
    autosave: true,
    // Field NAMES only (never values → no PHI in the prompt). Gender counts the toggle too.
    changedFields: (d, base) => {
      const names: string[] = [];
      if (d.firstName !== base.firstName) names.push(t('patient.demographics.firstName'));
      if (d.lastName !== base.lastName) names.push(t('patient.demographics.lastName'));
      if (d.lastNameAtBirth !== base.lastNameAtBirth) {
        names.push(t('patient.demographics.lastNameAtBirth'));
      }
      if (d.dateOfBirth !== base.dateOfBirth) names.push(t('patient.demographics.dateOfBirth'));
      if (d.sex !== base.sex) names.push(t('patient.demographics.sex'));
      if (d.genderIdentity !== base.genderIdentity || d.sameGenderAsSex !== base.sameGenderAsSex) {
        names.push(t('patient.demographics.genderIdentity'));
      }
      if (d.lifeStatus !== base.lifeStatus) names.push(t('patient.demographics.lifeStatus'));
      if (d.dateOfDeath !== base.dateOfDeath) names.push(t('patient.demographics.dateOfDeath'));
      if (d.causeOfDeath !== base.causeOfDeath) names.push(t('patient.demographics.causeOfDeath'));
      if (
        d.pregnancyDatingAnchor !== base.pregnancyDatingAnchor
        || d.pregnancyDatingBasis !== base.pregnancyDatingBasis
        || d.pregnancyDatingAsOf !== base.pregnancyDatingAsOf
      ) {
        names.push(t('patient.demographics.pregnancyDating', { defaultValue: 'Pregnancy dating' }));
      }
      if (d.primaryCareProvider !== base.primaryCareProvider) {
        names.push(t('patient.demographics.primaryCareProvider'));
      }
      if (d.referringProvider !== base.referringProvider) {
        names.push(t('patient.demographics.referringProvider'));
      }
      if (d.interpreterRequired !== base.interpreterRequired) {
        names.push(t('patient.demographics.interpreterRequired'));
      }
      if (d.interpreterLanguage !== base.interpreterLanguage) {
        names.push(t('patient.demographics.interpreterLanguage'));
      }
      return names;
    },
    onSave: (d) => updateDemographics.mutateAsync(toSavedVm(d)),
  });
  const { draft } = form;

  // PTV2-1328 — live graph-node preview. Inside the pedigree node panel a provider is present, so push
  // the draft's projected demographics to the engine node on every change: shape / life-status glyph /
  // name+age labels update BEFORE any save. A Discard reseeds the draft to the server values, which
  // re-publishes and reverts the node. No-op on the plain patient record (no provider).
  const nodePreview = useNodeGraphPreview();
  useEffect(() => {
    nodePreview?.publish('demographics', projectNodeDemographics(draft));
  }, [nodePreview, draft]);
  useEffect(() => {
    if (nodePreview === null) return undefined;
    return () => {
      nodePreview.clear('demographics');
    };
  }, [nodePreview]);

  // PTV2-1354 — an unborn (fetal) patient has no birth date; disable the DOB field so it can't be set
  // until the life status changes. Switching to UNBORN also drops any date already entered (below).
  const isUnborn = draft.lifeStatus === 'UNBORN';

  const showDateOfDeath =
    draft.lifeStatus !== null && DECEASED_STATUSES.has(draft.lifeStatus);
  const showCauseOfDeath =
    draft.lifeStatus !== null && CAUSE_OF_DEATH_STATUSES.has(draft.lifeStatus);
  const dateOfDeathLabel =
    draft.lifeStatus === 'DECEASED'
      ? t('patient.demographics.dateOfDeath')
      : t('patient.demographics.dateOfLoss');

  // Collapsed read-only summary (eye toggle). Built from the SAVED demographics, not the draft.
  const summaryRows = useMemo<CollapsibleCardSummaryRow[]>(() => {
    const dash = '—';
    const textRow = (label: string, raw: string | null): CollapsibleCardSummaryRow => {
      const filled = raw !== null && raw.trim().length > 0;
      return { label, value: filled ? raw : dash, filled };
    };
    return [
      textRow(t('patient.demographics.firstName'), demographics.firstName),
      textRow(t('patient.demographics.lastName'), demographics.lastName),
      textRow(t('patient.demographics.lastNameAtBirth'), demographics.lastNameAtBirth),
      textRow(t('patient.demographics.dateOfBirth'), demographics.dateOfBirth),
      {
        label: t('patient.demographics.sex'),
        value: demographics.sex !== null ? t(`patient.sex.${demographics.sex}`) : dash,
        filled: demographics.sex !== null,
      },
      textRow(t('patient.demographics.genderIdentity'), demographics.genderIdentity),
      {
        label: t('patient.demographics.lifeStatus'),
        value: demographics.lifeStatus !== null ? t(`patient.lifeStatus.${demographics.lifeStatus}`) : dash,
        filled: demographics.lifeStatus !== null,
      },
      textRow(t('patient.demographics.dateOfDeath'), demographics.dateOfDeath),
      textRow(t('patient.demographics.causeOfDeath'), demographics.causeOfDeath),
      textRow(t('patient.demographics.primaryCareProvider'), demographics.primaryCareProvider),
      textRow(t('patient.demographics.referringProvider'), demographics.referringProvider),
    ];
  }, [demographics, t]);

  return (
    <CollapsibleCard
      idPrefix="demographics"
      anchorId="personal-info"
      title={t('patient.demographics.personal')}
      defaultOpen
      summary={summaryRows}
      forceOpenSignal={forceOpenSignal}
    >
      <div
        ref={form.containerProps.ref}
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3"
      >
        <div className={flashClass('firstName')}>
          <TextField
            label={t('patient.demographics.firstName')}
            value={draft.firstName ?? ''}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, firstName: rawOrNull(next) }));
            }}
          />
        </div>
        <div className={flashClass('lastName')}>
          <TextField
            label={t('patient.demographics.lastName')}
            value={draft.lastName ?? ''}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, lastName: rawOrNull(next) }));
            }}
          />
        </div>
        <TextField
          label={t('patient.demographics.lastNameAtBirth')}
          value={draft.lastNameAtBirth ?? ''}
          onChange={(next) => {
            form.setDraft((d) => ({ ...d, lastNameAtBirth: rawOrNull(next) }));
          }}
        />
        <label className={cn('flex flex-col gap-1', flashClass('dateOfBirth'))}>
          <span className="text-xs font-medium text-muted-foreground">
            {t('patient.demographics.dateOfBirth')}
          </span>
          <DateInput
            value={draft.dateOfBirth ?? ''}
            disabled={isUnborn}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, dateOfBirth: next.length > 0 ? next : null }));
            }}
          />
        </label>
        <AgeField
          label={t('patient.demographics.age')}
          dateOfBirth={draft.dateOfBirth}
          disabled={isUnborn}
          onChange={(next) => {
            form.setDraft((d) => ({ ...d, dateOfBirth: next }));
          }}
        />
        <SelectField
          label={t('patient.demographics.lifeStatus')}
          value={draft.lifeStatus}
          options={LIFE_STATUS_OPTIONS}
          renderOption={(value) => t(`patient.lifeStatus.${value}`)}
          onChange={(next) => {
            // Keep the draft canonical: drop a stale date-of-death when the status isn't a loss, and a
            // stale date-of-birth when the patient is unborn (the DOB field is disabled for UNBORN).
            form.setDraft((d) => ({
              ...d,
              lifeStatus: next,
              dateOfBirth: next === 'UNBORN' ? null : d.dateOfBirth,
              dateOfDeath: DECEASED_STATUSES.has(next) ? d.dateOfDeath : null,
              causeOfDeath: CAUSE_OF_DEATH_STATUSES.has(next) ? d.causeOfDeath : null,
              // Dating is only authored while the pregnancy is ongoing (PTV2-1586). Leaving an
              // anchor on a born patient would keep a stale due date that nothing displays.
              pregnancyDatingAnchor: next === 'UNBORN' ? d.pregnancyDatingAnchor : null,
              pregnancyDatingBasis: next === 'UNBORN' ? d.pregnancyDatingBasis : null,
              pregnancyDatingAsOf: next === 'UNBORN' ? d.pregnancyDatingAsOf : null,
            }));
          }}
        />
        {isUnborn && (
          <PregnancyDatingInputs
            idPrefix={`personal-${guid}`}
            value={
              draft.pregnancyDatingAnchor === null
                ? null
                : {
                    anchor: parseDatingDate(draft.pregnancyDatingAnchor),
                    basis: draft.pregnancyDatingBasis,
                    asOf: parseDatingDate(draft.pregnancyDatingAsOf),
                  }
            }
            onChange={(next) => {
              form.setDraft((d) => ({
                ...d,
                pregnancyDatingAnchor: next === null ? null : formatDatingDate(next.anchor),
                pregnancyDatingBasis: next === null ? null : next.basis,
                pregnancyDatingAsOf: next === null ? null : formatDatingDate(next.asOf ?? null),
              }));
            }}
          />
        )}
        {showDateOfDeath && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {dateOfDeathLabel}
            </span>
            <DateInput
              value={draft.dateOfDeath ?? ''}
              onChange={(next) => {
                form.setDraft((d) => ({ ...d, dateOfDeath: next.length > 0 ? next : null }));
              }}
            />
          </label>
        )}
        {showCauseOfDeath && (
          <TextField
            label={t('patient.demographics.causeOfDeath')}
            value={draft.causeOfDeath ?? ''}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, causeOfDeath: rawOrNull(next) }));
            }}
          />
        )}
        <div className="flex flex-col gap-2">
          <div className={flashClass('sex')}>
            <SexField
              label={t('patient.demographics.sex')}
              value={draft.sex}
              onChange={(next) => {
                form.setDraft((d) => ({ ...d, sex: next }));
              }}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none cursor-pointer hover:text-foreground transition-colors w-fit">
            <input
              type="checkbox"
              checked={draft.sameGenderAsSex}
              onChange={(event) => {
                const checked = event.target.checked;
                form.setDraft((d) => ({ ...d, sameGenderAsSex: checked }));
              }}
              className="h-3.5 w-3.5 rounded border-border accent-primary cursor-pointer"
            />
            <span>{t('patient.demographics.sameGenderAsSex')}</span>
          </label>
        </div>
        {!draft.sameGenderAsSex && (
          <GenderIdentityField
            label={t('patient.demographics.genderIdentity')}
            value={draft.genderIdentity}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, genderIdentity: next }));
            }}
          />
        )}
        <TextField
          label={t('patient.demographics.primaryCareProvider')}
          value={draft.primaryCareProvider ?? ''}
          onChange={(next) => {
            form.setDraft((d) => ({ ...d, primaryCareProvider: rawOrNull(next) }));
          }}
        />
        <TextField
          label={t('patient.demographics.referringProvider')}
          value={draft.referringProvider ?? ''}
          onChange={(next) => {
            form.setDraft((d) => ({ ...d, referringProvider: rawOrNull(next) }));
          }}
        />
        <InterpreterRequiredField
          label={t('patient.demographics.interpreterRequired')}
          value={draft.interpreterRequired}
          onChange={(next) => {
            // Keep the draft canonical: clear the language when interpreter isn't required.
            form.setDraft((d) => ({
              ...d,
              interpreterRequired: next,
              interpreterLanguage: next === 'Y' ? d.interpreterLanguage : null,
            }));
          }}
        />
        {draft.interpreterRequired === 'Y' && (
          <TextField
            label={t('patient.demographics.interpreterLanguage')}
            value={draft.interpreterLanguage ?? ''}
            onChange={(next) => {
              form.setDraft((d) => ({ ...d, interpreterLanguage: rawOrNull(next) }));
            }}
          />
        )}
      </div>
      <DraftFooter form={form} label="Personal information" />
    </CollapsibleCard>
  );
}

/**
 * While typing, keep the value verbatim so interior/trailing spaces survive — a
 * controlled input that trims on every keystroke can never accept a space
 * (PTV2-1377: "Mary Anne", "Abdul Rahman"). Only a fully-empty field collapses
 * to null; surrounding whitespace is trimmed at save time via {@link normalizeText}.
 */
function rawOrNull(value: string): string | null {
  return value.length > 0 ? value : null;
}

/** Save-time normalization: trim surrounding whitespace, empty → null. */
function normalizeText(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
